import { useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { vestigasApi } from '~/services/kyClient';

import { type FilterConfig } from './types';

export const exportableStandardFields = [
  'accounting_references',
  'article_name',
  'article_number',
  'authorized_accounting_references',
  'authorized_sites',
  'buyer_order',
  'buyer',
  'carrier',
  'carrier_number',
  'confirmed_site',
  'created_on',
  'customer_number',
  'delivery_terms',
  'dln_date',
  'dln_nr',
  'gross_weight', // formatted value combining amount and unit
  'gross_weight_amount',
  'gross_weight_unit',
  'id',
  'license_plate',
  'loading_location_number',
  'loading_location',
  'modified_on',
  'movement_means',
  'net_weight', // formatted value combining amount and unit
  'process_state',
  'project',
  'quantity_amount',
  'quantity_unit',
  'recipient',
  'remaining_items_count',
  'seller_order',
  'seller',
  'signed_by_email',
  'source',
  'source_user',
  'supplier_assigned_site_address',
  'supplier_assigned_site_name',
  'supplier',
  'tare_weight', // formatted value combining amount and unit
  'tare_weight_amount',
  'tare_weight_unit',
  'trader',
  'weight', // (net weight) formatted value combining amount and unit
  'weight_amount',
  'weight_unit',
];

export type ExportableStandardField = (typeof exportableStandardFields)[number];

export type ExportConfigItem = {
  customFieldId?: UUID;
  name:
    | ExportableStandardField
    | 'custom_field_global_level'
    | 'custom_field_item_level';
};

export type ExportFormat = 'csv' | 'excel';

export type DeliveryNoteAssetsSearchParams = {
  assetIds: UUID[];
  exportConfig: ExportConfigItem[];
  exportOrder: ExportableStandardField[];
  filterConfig: FilterConfig;
  format: ExportFormat;
};

type ExportAssetResponse = {
  code: string;
  message: string;
  requestId: string;
};

/**
 * Sends a request to initiate report asset.
 *
 * @param {DeliveryNoteAssetsSearchParams} searchParams - Parameters for filtering and configuring the report
 * @returns {Promise<ExportAssetResponse>} The response containing request_id
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Delivery-Note/operation/request_delivery_note_export_asset_delivery_note_export_request_post
 */
export const requestAssetExport = async (
  searchParams: DeliveryNoteAssetsSearchParams,
) => {
  const response = await vestigasApi
    .post(ENDPOINT.DELIVERY_NOTE.REQUEST_ASSET_EXPORT(), {
      json: searchParams,
    })
    .json<ExportAssetResponse>();

  return response;
};

/**
 * React Query based custom hook for requesting export of delivery note asset.
 *
 * @returns {UseMutationResult<ExportAssetResponse, Error, DeliveryNoteAssetsSearchParams>}
 */
export const useMutationRequestDeliveryNoteAssetExport = () => {
  return useMutation({
    mutationFn: async (searchParams: DeliveryNoteAssetsSearchParams) =>
      requestAssetExport(searchParams),
    onMutate({ format }) {
      toast.loading(
        format === 'csv'
          ? 'CSV-Lieferung wird geladen...'
          : 'Excel-Lieferung wird geladen...',
      );
    },
    onSuccess(_, { format }) {
      toast.remove();
      toast.success(
        format === 'csv'
          ? 'CSV-Lieferung wurde erfolgreich abgeschlossen!'
          : 'Excel-Lieferung wurde erfolgreich abgeschlossen!',
      );
    },
  });
};
